import { create } from 'zustand';
import type { User, Raffle, RaffleDetail, Transaction, ReferralStats, Jackpot } from '@/types';
import { api } from '@/services/api';

interface AppState {
  // User state
  user: User | null;
  loading: boolean;
  error: string | null;
  isWebMode: boolean;

  // Raffles state
  raffles: Raffle[];
  currentRaffle: RaffleDetail | null;

  // Transactions state
  transactions: Transaction[];

  // Referral state
  referralStats: ReferralStats | null;

  // Jackpot state
  jackpot: Jackpot | null;

  // Actions
  fetchUser: () => Promise<void>;
  fetchRaffles: () => Promise<void>;
  fetchRaffleDetails: (id: number) => Promise<void>;
  fetchTransactions: () => Promise<void>;
  fetchReferralStats: () => Promise<void>;
  fetchJackpot: () => Promise<void>;
  placeBet: (amount: number) => Promise<void>;
  enterRaffle: (raffleId: number, userTicketId: number) => Promise<void>;
  createTestDeposit: (amount: number) => Promise<void>;
  webLogin: (email: string, password: string) => Promise<void>;
  webRegister: (email: string, password: string, username?: string) => Promise<void>;
  logout: () => Promise<void>;
  setError: (error: string | null) => void;
  clearError: () => void;
}

const isTelegramApp = typeof window !== 'undefined' &&
  Boolean((window as any).Telegram?.WebApp?.initData);

export const useStore = create<AppState>((set, get) => ({
  // Initial state
  user: null,
  loading: false,
  error: null,
  isWebMode: !isTelegramApp,
  raffles: [],
  currentRaffle: null,
  transactions: [],
  referralStats: null,
  jackpot: null,

  // Actions
  fetchUser: async () => {
    try {
      set({ loading: true, error: null });
      const user = await api.getCurrentUser();
      set({ user, loading: false });
    } catch (error: any) {
      console.error('[STORE] Fetch user error:', error);
      set({
        error: error.response?.data?.detail || 'Failed to fetch user',
        loading: false
      });
    }
  },

  fetchRaffles: async () => {
    try {
      set({ loading: true, error: null });
      const raffles = await api.getRaffles();
      set({ raffles, loading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to fetch raffles',
        loading: false
      });
    }
  },

  fetchRaffleDetails: async (id: number) => {
    try {
      set({ loading: true, error: null });
      const raffle = await api.getRaffle(id);
      set({ currentRaffle: raffle, loading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to fetch raffle details',
        loading: false
      });
    }
  },

  fetchTransactions: async () => {
    try {
      set({ loading: true, error: null });
      const transactions = await api.getTransactions();
      set({ transactions, loading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to fetch transactions',
        loading: false
      });
    }
  },

  fetchReferralStats: async () => {
    try {
      set({ loading: true, error: null });
      const referralStats = await api.getReferralStats();
      set({ referralStats, loading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to fetch referral stats',
        loading: false
      });
    }
  },

  enterRaffle: async (raffleId: number, userTicketId: number) => {
    try {
      set({ loading: true, error: null });
      await api.enterRaffle({ raffle_id: raffleId, user_ticket_id: userTicketId });

      // Refresh user and raffles
      await get().fetchUser();
      await get().fetchRaffles();

      set({ loading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to enter raffle',
        loading: false
      });
      throw error;
    }
  },

  createTestDeposit: async (amount: number) => {
    try {
      set({ loading: true, error: null });
      await api.createTestDeposit({ amount });

      // Refresh user
      await get().fetchUser();

      set({ loading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to create deposit',
        loading: false
      });
      throw error;
    }
  },

  fetchJackpot: async () => {
    try {
      set({ loading: true, error: null });
      const jackpot = await api.getCurrentJackpot();
      set({ jackpot, loading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to fetch jackpot',
        loading: false
      });
    }
  },

  placeBet: async (amount: number) => {
    try {
      set({ loading: true, error: null });
      await api.placeJackpotBet({ amount });

      // Refresh user and jackpot
      await get().fetchUser();
      await get().fetchJackpot();

      set({ loading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Failed to place bet',
        loading: false
      });
      throw error;
    }
  },

  webLogin: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      const { user } = await api.webLogin(email, password);
      set({ user: user as any, loading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Неверный email или пароль',
        loading: false,
      });
      throw error;
    }
  },

  webRegister: async (email: string, password: string, username?: string) => {
    set({ loading: true, error: null });
    try {
      const { user } = await api.webRegister(email, password, username);
      set({ user: user as any, loading: false });
    } catch (error: any) {
      set({
        error: error.response?.data?.detail || 'Ошибка регистрации',
        loading: false,
      });
      throw error;
    }
  },

  logout: async () => {
    try {
      await api.logout();
    } finally {
      set({ user: null });
    }
  },

  setError: (error: string | null) => set({ error }),
  clearError: () => set({ error: null }),
}));
